import { Headline, Text } from '@oneaudi/unified-web-components';
import { useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import styled from 'styled-components';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { eecDataMerger } from '~/app/compositions/content/eec/eec-label-helper';
import { EecLabelSection } from '~/app/compositions/content/eec/eec-label-section';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { TechnicalDataSections } from '~/app/pages/configurator/components/technical-data-dialog/components/technical-data-dialog-content/components/technical-data-sections';
import { useConfiguredCarTechnicalDataSections } from '~/app/pages/configurator/components/technical-data-dialog/components/technical-data-dialog-content/components/technical-data-sections/configured-car-technical-data';
import { useModelTechnicalDataSection } from '~/app/pages/configurator/components/technical-data-dialog/components/technical-data-dialog-content/components/technical-data-sections/model-technical-data';
import { ErrorMessage } from '~/components/errors';
import { useFootnotes } from '~/components/footnotes';
import { CAE_TECHDATA_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import {
	ConfiguredCarInput,
	ConfiguredCarTechnicalDataQuery,
	ModelTechnicalDataQuery,
} from '~/graphql/__generated__/graphql';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import {
	useConfiguredCarTechnicalDataLazyQuery,
	useModelTechnicalDataLazyQuery,
} from '~/graphql/hooks/use-configured-car-queries';
import { getModelIdStringFromPr7Extension } from '~/graphql/mapper/model-id-string';
import { CatalogItemType } from '~/graphql/types';

export function ModelTechnicalDataDialogContent({
	driveTrain,
	triggerQuery,
}: {
	driveTrain: CatalogItemType['driveTrain'];
	triggerQuery: boolean;
}) {
	const { i18n } = useI18n();
	const { carInfo } = useCatalog();
	const { carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();

	const { fetchModelData, data, loading } = useModelTechnicalDataLazyQuery();

	useEffect(() => {
		if (triggerQuery) {
			fetchModelData({
				variables: {
					configuredCar: carIdentifier,
				},
			});
		}
	}, [triggerQuery]);

	const technicalData = useMemo(() => {
		if (loading || !data) return;
		return data?.configuredCar.carlineStructureCarline?.trimlines
			.flatMap((trimlines) => trimlines.models)
			.find((d) => getModelIdStringFromPr7Extension(d.modelId) === driveTrain.modelIdString)?.technicalDataV2;
	}, [
		data,
		loading,
		data?.configuredCar,
		data?.configuredCar?.carlineStructureCarline,
		data?.configuredCar?.carlineStructureCarline?.trimlines,
	]);

	const {
		fetchTechnicalData,
		loading: technicalDataLoading,
		data: configuredCarTechData,
	} = useConfiguredCarTechnicalDataLazyQuery();

	useEffect(() => {
		if (triggerQuery) {
			fetchTechnicalData({
				variables: {
					configuredCar: {
						...carIdentifier,
						model: {
							...carIdentifier.model,
							code: driveTrain.featureId,
							version: driveTrain.modelId.version,
						},
					},
				},
			});
		}
	}, [triggerQuery]);

	const wltpInfoMerge = useMemo(() => {
		if (!config.wltpInfo) return;
		if (technicalDataLoading || !configuredCarTechData) return;
		const mergeData = eecDataMerger(config.wltpInfo, configuredCarTechData);
		return mergeData;
	}, [configuredCarTechData, config.wltpInfo]);

	return (
		<>
			<TechnicalDataDialogTitle
				carline={carInfo?.carline ?? ''}
				model={driveTrain.name}
				consumptionDisclaimer={driveTrain.techData?.consumptionAndEmission?.disclaimers.map((a) => a.value)}
			/>
			{loading && <LoadingSkeleton />}
			{technicalData && <ModelTechnicalDataSections technicalData={technicalData} />}
			{!loading &&
				!technicalDataLoading &&
				configuredCarTechData &&
				technicalData &&
				config.showEECLabel &&
				config.showEECLabelEngine &&
				config.wltpInfo && (
					<EecLabelSection url={wltpInfoMerge?.urls?.svgUrl || undefined} eecURL={wltpInfoMerge?.url?.url} />
				)}

			{!loading && !technicalData && <ErrorMessage text={i18n('ngc.technicalData.notAvailable')} />}
		</>
	);
}

function ModelTechnicalDataSections({
	technicalData,
}: {
	technicalData: NonNullable<
		NonNullable<
			ModelTechnicalDataQuery['configuredCar']['carlineStructureCarline']
		>['trimlines'][0]['models'][0]['technicalDataV2']
	>;
}) {
	const sections = useModelTechnicalDataSection(technicalData);
	return <TechnicalDataSections sections={sections} />;
}

export function ConfiguredCarTechnicalDataDialogContent({
	carline,
	model,
	carIdentifier,
	triggerQuery,
}: {
	carline: string;
	model: string;
	carIdentifier: ConfiguredCarInput;
	triggerQuery: boolean;
}) {
	const { i18n } = useI18n();
	const { fetchTechnicalData, loading, data: technicalData } = useConfiguredCarTechnicalDataLazyQuery();

	useEffect(() => {
		if (triggerQuery) {
			fetchTechnicalData({
				variables: {
					configuredCar: {
						...carIdentifier,
					},
				},
			});
		}
	}, [triggerQuery]);

	return (
		<>
			<TechnicalDataDialogTitle carline={carline} model={model} />
			{loading && <LoadingSkeleton />}
			{technicalData && <ConfiguredCarTechnicalDataSections data={technicalData} loading={loading} />}
			{!loading && !technicalData && <ErrorMessage text={i18n('ngc.technicalData.notAvailable')} />}
		</>
	);
}

function ConfiguredCarTechnicalDataSections({
	data,
	loading,
}: {
	data: ConfiguredCarTechnicalDataQuery;
	loading: boolean;
}) {
	const sections = useConfiguredCarTechnicalDataSections(data);
	const { config } = useMarketConfig();
	const td = data.configuredCar.technicalDataV2;
	if (!td) return null;

	return (
		<>
			<TechnicalDataSections sections={sections} />
			{!loading && config.showEECLabel && config.wltpInfo && data && (
				<EecLabelSection
					url={td?.envkv?.urls?.svgUrl || ''}
					eecURL={data?.configuredCar?.media?.eec?.eecLabelImageUrl || ''}
				/>
			)}
		</>
	);
}

function TechnicalDataDialogTitle({
	carline,
	model,
	consumptionDisclaimer,
}: {
	carline: string;
	model: string;
	consumptionDisclaimer?: string[];
}) {
	const { getReference } = useFootnotes();
	const { config } = useMarketConfig();
	const { i18n } = useI18n();
	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog(consumptionDisclaimer, CAE_TECHDATA_REMARK);
	}
	return (
		<div className="flex flex-col gap-1">
			<Headline tag={'label'} variant={{ type: 'headline', order: '1', weight: 'bold' }}>
				{i18n('ngc.actions.technicalData')}
			</Headline>
			<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
				{i18n('ngc.technicalData.carlineModel', { carline, model })}
				<FootnoteReferenceNgc reference={getReference(CAE_TECHDATA_REMARK)} />
			</Text>
		</div>
	);
}

function LoadingSkeleton() {
	return (
		<LoadingSkeletonStyles>
			<Skeleton height={256} />
			<Skeleton height={256} />
			<Skeleton height={256} />
			<Skeleton height={256} />
			<Skeleton height={256} />
		</LoadingSkeletonStyles>
	);
}

const LoadingSkeletonStyles = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4rem;
`;
